import axios from "axios";

export const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND}/api`;

// Resolve a relative "/api/files/..." url to an absolute one
export const mediaUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${BACKEND}${u}`;
};

// Downscaled variant for grids/galleries (the backend caches each width once).
export const sizedUrl = (u, w) => {
  const base = mediaUrl(u);
  if (!base || !w) return base;
  return `${base}${base.includes("?") ? "&" : "?"}w=${w}`;
};

// Responsive candidates so phones download a smaller file than desktops.
export const srcSetFor = (u, widths) =>
  u ? widths.map((w) => `${sizedUrl(u, w)} ${w}w`).join(", ") : undefined;

export const api = axios.create({ baseURL: API, withCredentials: true });

// Retry public GETs twice on network errors / 5xx so a single flaky response
// can't leave a page stuck empty.
api.interceptors.response.use(undefined, async (error) => {
  const cfg = error.config || {};
  const method = (cfg.method || "get").toLowerCase();
  const status = error.response?.status;
  const retriable = method === "get" && (!error.response || status >= 500);
  cfg.__retry = cfg.__retry || 0;
  if (retriable && cfg.__retry < 2) {
    cfg.__retry += 1;
    await new Promise((r) => setTimeout(r, 400 * cfg.__retry));
    return api(cfg);
  }
  return Promise.reject(error);
});

// public
export const fetchProjects = () => api.get("/projects").then((r) => r.data);
export const fetchProject = (slug) => api.get(`/projects/${slug}`).then((r) => r.data);
export const fetchSiteContent = () => api.get("/site-content").then((r) => r.data);

// auth
export const authGate = (username, password) =>
  api.post("/auth/gate", { username, password }).then((r) => r.data);
export const requestToken = (gateToken) =>
  api.post("/auth/request-token", {}, { headers: { Authorization: `Bearer ${gateToken}` } }).then((r) => r.data);
export const verifyToken = (token) => api.post("/auth/verify-token", { token }).then((r) => r.data);
export const fetchMe = () => api.get("/auth/me").then((r) => r.data);
export const logout = () => api.post("/auth/logout").then((r) => r.data);

// admin
export const createProject = (body) => api.post("/admin/projects", body).then((r) => r.data);
export const updateProject = (id, body) => api.put(`/admin/projects/${id}`, body).then((r) => r.data);
export const deleteProject = (id) => api.delete(`/admin/projects/${id}`).then((r) => r.data);
export const reorderProjects = (order) => api.post("/admin/projects/reorder", { order }).then((r) => r.data);
export const uploadImage = (projectId, file, onProgress, dims) => {
  const fd = new FormData();
  fd.append("file", file);
  if (dims?.width) fd.append("width", Math.round(dims.width));
  if (dims?.height) fd.append("height", Math.round(dims.height));
  return api
    .post(`/admin/projects/${projectId}/images`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: onProgress,
    })
    .then((r) => r.data);
};
export const setPoster = (imageId, blob) => {
  const fd = new FormData();
  fd.append("file", blob, "poster.jpg");
  return api
    .post(`/admin/images/${imageId}/poster`, fd, { headers: { "Content-Type": "multipart/form-data" } })
    .then((r) => r.data);
};
export const deleteImage = (id) => api.delete(`/admin/images/${id}`).then((r) => r.data);
export const moveImage = (id, projectId) =>
  api.post(`/admin/images/${id}/move`, { project_id: projectId }).then((r) => r.data);
export const reorderImages = (projectId, order) =>
  api.post(`/admin/projects/${projectId}/images/reorder`, { order }).then((r) => r.data);
export const updateSiteContent = (body) => api.put("/admin/site-content", body).then((r) => r.data);
export const uploadHeroImage = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/admin/site-content/hero-image", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const rotateImage = (id) => api.post(`/admin/images/${id}/rotate`).then((r) => r.data);
export const rotateImagesBulk = (ids) => api.post(`/admin/images/rotate-bulk`, { ids }).then((r) => r.data);

// categories
export const fetchSecurityInfo = () => api.get("/admin/security").then((r) => r.data);
export const setLoginAlerts = (enabled) =>
  api.post("/admin/security/alerts", { enabled }).then((r) => r.data);
export const changeGatePassword = (current_password, new_password) =>
  api.post("/admin/gate-password", { current_password, new_password }).then((r) => r.data);
export const suggestGatePassword = () => api.post("/admin/gate-password/suggest").then((r) => r.data);

export const fetchCategories = () => api.get("/categories").then((r) => r.data);export const createCategory = (name, color) => api.post("/admin/categories", { name, color }).then((r) => r.data);
export const updateCategory = (id, body) => api.put(`/admin/categories/${id}`, body).then((r) => r.data);
export const deleteCategory = (id) => api.delete(`/admin/categories/${id}`).then((r) => r.data);
export const reorderCategories = (order) => api.post("/admin/categories/reorder", { order }).then((r) => r.data);

export const formatApiError = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  return e?.message || "Something went wrong.";
};
